import type { CrmType, SupplierDto } from '@ops/entities/operation-item';
import {
  useSearchSuppliers,
  useUpdateSuppliers,
} from '@ops/entities/operation-item';
import { toastMutation } from '@ops/shared/lib/toast-mutation';
import { Star, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  Select,
  Text,
} from '@/shared/ui';
import { addSupplier, removeSupplier } from '../model/schema';
import css from './supplier-change-dialog.module.scss';

type SupplierChangeDialogProps = {
  open: boolean;
  itineraryItemIds: string[];
  initialSuppliers: SupplierDto[];
  onClose: () => void;
  // Fired after a successful save (not on cancel) — used by the bulk caller to
  // clear the multi-select once the change is applied.
  onSaved?: () => void;
};

const crmTypeOptions = [
  { value: '1', label: 'Company' },
  { value: '2', label: 'Contact' },
];

export const SupplierChangeDialog = ({
  open,
  itineraryItemIds,
  initialSuppliers,
  onClose,
  onSaved,
}: SupplierChangeDialogProps) => {
  const [suppliers, setSuppliers] = useState<SupplierDto[]>(initialSuppliers);
  const [crmType, setCrmType] = useState<CrmType>('1');
  const [searchTerm, setSearchTerm] = useState('');
  const updateSuppliers = useUpdateSuppliers();
  const search = useSearchSuppliers(
    { crmType, name: searchTerm },
    { enabled: open && searchTerm.length > 0 }
  );

  // Seed the editable list once per open. Re-seeding on every `initialSuppliers`
  // reference change would let a background list refetch wipe in-progress edits
  // (primary / selection) before the user hits Save.
  const seededForOpen = useRef(false);
  useEffect(() => {
    if (open && !seededForOpen.current) {
      setSuppliers(initialSuppliers);
      seededForOpen.current = true;
    } else if (!open) {
      seededForOpen.current = false;
      setSearchTerm('');
      setCrmType('1');
    }
  }, [open, initialSuppliers]);

  // Primary is single-select: clicking designates that supplier as the primary
  // and clears the rest — there is always exactly one primary, no toggle-off.
  const setPrimary = (id: string) =>
    setSuppliers((current) =>
      current.map((s) => ({ ...s, primary: s.id === id }))
    );

  const toggleSelected = (id: string) =>
    setSuppliers((current) =>
      current.map((s) => (s.id === id ? { ...s, selected: !s.selected } : s))
    );

  const handleAdd = (candidate: { id: string; name: string }) => {
    setSuppliers((current) =>
      addSupplier(current, {
        id: candidate.id,
        name: candidate.name,
        crmType,
      })
    );
    setSearchTerm('');
  };

  const handleRemove = (id: string) => {
    setSuppliers((current) => removeSupplier(current, id));
  };

  const handleSubmit = () => {
    updateSuppliers.mutate(
      {
        itineraryItemIds,
        suppliers,
      },
      toastMutation(
        { success: 'Suppliers updated', error: 'Couldn’t update suppliers' },
        {
          onSuccess: () => {
            onSaved?.();
            onClose();
          },
        }
      )
    );
  };

  const suggestions = (search.data ?? []).filter(
    (row) => !suppliers.some((s) => s.id === row.id)
  );

  return (
    <Modal onClose={onClose} open={open}>
      <ModalHeader onClose={onClose} title="Change Suppliers" />
      <div className={css.root}>
        <div className={css.adder}>
          <div className={css.adderRow}>
            <Select
              onChange={(e) => setCrmType(e.target.value as CrmType)}
              options={crmTypeOptions}
              size="sm"
              value={crmType}
            />
            <Input
              aria-label="Search suppliers"
              className={css.adderSearch}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={
                crmType === '1' ? 'Search companies…' : 'Search contacts…'
              }
              value={searchTerm}
            />
          </div>
          {searchTerm.length > 0 && (
            <ul className={css.suggestions}>
              {search.isLoading && (
                <li className={css.suggestionEmpty}>Searching…</li>
              )}
              {!search.isLoading && suggestions.length === 0 && (
                <li className={css.suggestionEmpty}>No matches</li>
              )}
              {suggestions.map((row) => (
                <li className={css.suggestionItem} key={row.id}>
                  <Button
                    onClick={() => handleAdd({ id: row.id, name: row.name })}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    {row.name || '(unnamed)'}
                    {' · '}
                    {row.crmType === '1' ? 'Company' : 'Contact'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {suppliers.length === 0 ? (
          <Text className={css.empty} size="sm" variant="muted">
            No suppliers attached to this item.
          </Text>
        ) : (
          <ul className={css.list}>
            {suppliers.map((supplier) => (
              <li
                aria-label={supplier.name}
                className={joinCss(
                  css.row,
                  supplier.primary && css.rowIsPrimary
                )}
                key={supplier.id}
              >
                <span className={css.name}>{supplier.name}</span>
                <Checkbox
                  checked={supplier.selected}
                  className={css.flag}
                  label="Selected"
                  onChange={() => toggleSelected(supplier.id)}
                />
                <Button
                  aria-label={
                    supplier.primary ? 'Primary supplier' : 'Make primary'
                  }
                  icon={<Star style={{ height: 14, width: 14 }} />}
                  onClick={() => setPrimary(supplier.id)}
                  size="sm"
                  type="button"
                  variant={supplier.primary ? 'primary' : 'outline'}
                >
                  Primary
                </Button>
                <Button
                  aria-label="Remove supplier"
                  className={css.controls}
                  icon={<Trash2 style={{ height: 14, width: 14 }} />}
                  onClick={() => handleRemove(supplier.id)}
                  size="sm"
                  type="button"
                  variant="ghost"
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      <ModalFooter>
        <Button onClick={onClose} type="button" variant="outline">
          Cancel
        </Button>
        <Button
          disabled={updateSuppliers.isPending}
          onClick={handleSubmit}
          type="button"
          variant="primary"
        >
          {updateSuppliers.isPending ? 'Saving…' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
